/**
 * Create Contact Modal
 * Sources are fetched from Acquisition Command settings
 */
import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { Plus, User, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SERVICE_TYPES = [
  { value: "kitchen_remodel", label: "Kitchen Remodel" },
  { value: "bathroom_remodel", label: "Bathroom Remodel" },
  { value: "basement_remodel", label: "Basement Remodel" },
  { value: "addition", label: "Addition" },
  { value: "roofing", label: "Roofing" },
  { value: "siding", label: "Siding" },
  { value: "windows", label: "Windows" },
  { value: "flooring", label: "Flooring" },
  { value: "painting", label: "Painting" },
  { value: "general_contracting", label: "General Contracting" },
  { value: "other", label: "Other" },
];

const TIMELINES = [
  { value: "asap", label: "ASAP" },
  { value: "1_month", label: "Within 1 Month" },
  { value: "3_months", label: "1-3 Months" },
  { value: "6_months", label: "3-6 Months" },
  { value: "not_sure", label: "Not Sure" },
];

const CreateContactModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    address_street: "",
    address_city: "",
    address_state: "",
    address_zip: "",
    source: "", // Will be set from enabled lead sources
    service_type: "",
    service_description: "",
    budget_min: "",
    budget_max: "",
    timeline: "",
    estimated_value: "",
    notes: ""
  });

  // Fetch lead sources from Acquisition Command settings
  useEffect(() => {
    if (isOpen) {
      loadLeadSources();
    }
  }, [isOpen]);

  const loadLeadSources = async () => {
    setLoadingSources(true);
    try {
      // Fetch active lead sources from settings
      const response = await API.get("/lead-sources?active=true");
      const sources = response.data || [];
      setLeadSources(sources);
      
      // Set default source if available and not already set
      if (sources.length > 0 && !formData.source) {
        setFormData(prev => ({ ...prev, source: sources[0].lead_source_id }));
      }
    } catch (error) {
      console.error("Failed to load lead sources:", error);
      // Don't show error toast - just leave dropdown empty
    } finally {
      setLoadingSources(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.first_name) {
      toast.error("First name is required");
      return;
    }
    if (!formData.phone && !formData.email) {
      toast.error("Phone or email is required");
      return;
    }
    if (!formData.source) {
      toast.error("Lead source is required");
      return;
    }

    setLoading(true);
    try {
      // Find the source name for storage
      const selectedSource = leadSources.find(s => s.lead_source_id === formData.source);
      
      // Clean up data
      const payload = {
        ...formData,
        source: formData.source, // Store lead_source_id
        source_name: selectedSource?.name || formData.source, // Also store display name
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
      };

      const response = await API.post("/contacts", payload);
      toast.success("Contact created successfully");
      
      // Get the created contact
      const createdContact = response.data;
      
      // Reset form (source will be set by loadLeadSources on next open)
      setFormData({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        address_street: "",
        address_city: "",
        address_state: "",
        address_zip: "",
        source: "",
        service_type: "",
        service_description: "",
        budget_min: "",
        budget_max: "",
        timeline: "",
        estimated_value: "",
        notes: ""
      });
      
      // Pass created contact to parent for scheduling
      onSuccess(createdContact);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("A contact with this phone/email already exists");
      } else {
        toast.error("Failed to create contact");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-emerald-400" />
            New Contact
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">First Name *</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => handleChange("first_name", e.target.value)}
                placeholder="John"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-slate-400">Last Name</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => handleChange("last_name", e.target.value)}
                placeholder="Smith"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder="(555) 123-4567"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-slate-400">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="john@email.com"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <Label className="text-slate-400">Street Address</Label>
            <Input
              value={formData.address_street}
              onChange={(e) => handleChange("address_street", e.target.value)}
              placeholder="123 Main St"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-400">City</Label>
              <Input
                value={formData.address_city}
                onChange={(e) => handleChange("address_city", e.target.value)}
                placeholder="Kansas City"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-slate-400">State</Label>
              <Input
                value={formData.address_state}
                onChange={(e) => handleChange("address_state", e.target.value)}
                placeholder="KS"
                maxLength={2}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-slate-400">ZIP</Label>
              <Input
                value={formData.address_zip}
                onChange={(e) => handleChange("address_zip", e.target.value)}
                placeholder="66102"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Source & Service */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">Lead Source *</Label>
              <Select value={formData.source} onValueChange={(v) => handleChange("source", v)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500">
                  <SelectValue placeholder={loadingSources ? "Loading..." : "Select source"} />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {loadingSources ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                    </div>
                  ) : leadSources.length > 0 ? (
                    leadSources.map(s => (
                      <SelectItem key={s.lead_source_id} value={s.lead_source_id}>
                        {s.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-sm text-slate-400 text-center">
                      No lead sources enabled.<br/>
                      <span className="text-xs">Enable sources in Acquisition Command</span>
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400">Service Type</Label>
              <Select value={formData.service_type} onValueChange={(v) => handleChange("service_type", v)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {SERVICE_TYPES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Budget & Timeline */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-400">Budget Min</Label>
              <Input
                type="number"
                value={formData.budget_min}
                onChange={(e) => handleChange("budget_min", e.target.value)}
                placeholder="$0"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-slate-400">Budget Max</Label>
              <Input
                type="number"
                value={formData.budget_max}
                onChange={(e) => handleChange("budget_max", e.target.value)}
                placeholder="$0"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-slate-400">Timeline</Label>
              <Select value={formData.timeline} onValueChange={(v) => handleChange("timeline", v)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {TIMELINES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-slate-400">Service Description</Label>
            <Textarea
              value={formData.service_description}
              onChange={(e) => handleChange("service_description", e.target.value)}
              placeholder="Describe what the customer needs..."
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              rows={3}
            />
          </div>

          {/* Value & Notes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">Estimated Value</Label>
              <Input
                type="number"
                value={formData.estimated_value}
                onChange={(e) => handleChange("estimated_value", e.target.value)}
                placeholder="$0"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          <div>
            <Label className="text-slate-400">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Any additional notes..."
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-500">
            <Plus className="h-4 w-4 mr-2" />
            Create Contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateContactModal;
